/**
 * Extract variables from template text (supports both positional and named)
 * @param {string} templateText - Template body text
 * @returns {Array<string>} Array of variable keys (e.g., ['1', '2', '3'] or ['customer_name', 'order_id'])
 */
export const extractVariables = templateText => {
  if (!templateText) return [];

  // Pattern to match both positional {{1}} and named {{customer_name}}
  const variablePattern = /\{\{([a-zA-Z0-9_]+)\}\}/g;

  // Use matchAll to find all matches
  const matchArray = Array.from(templateText.matchAll(variablePattern));
  const matches = matchArray.map(match => match[1]);

  // Remove duplicates
  const uniqueMatches = [...new Set(matches)];

  // Sort: numbers first (numerically), then strings (alphabetically)
  return uniqueMatches.sort((a, b) => {
    const aIsNumber = /^\d+$/.test(a);
    const bIsNumber = /^\d+$/.test(b);

    if (aIsNumber && bIsNumber) {
      // Both are numbers - sort numerically
      return parseInt(a, 10) - parseInt(b, 10);
    }
    if (aIsNumber) {
      // a is number, b is string - numbers come first
      return -1;
    }
    if (bIsNumber) {
      // a is string, b is number - numbers come first
      return 1;
    }
    // Both are strings - sort alphabetically
    return a.localeCompare(b);
  });
};

/**
 * Categorize variables as static or dynamic
 * @param {Array<string>} variables - Array of variable positions
 * @param {Object} mappings - Variable mappings object
 * @returns {Object} Categorized variables
 */
export const categorizeVariables = (variables, mappings) => {
  if (!variables || !mappings) {
    return { dynamic: [], static: [] };
  }

  const dynamic = [];
  const static_vars = [];

  variables.forEach(position => {
    const mapping = mappings[position];
    if (mapping) {
      if (mapping.type === 'dynamic') {
        dynamic.push(position);
      } else if (mapping.type === 'static') {
        static_vars.push(position);
      }
    }
  });

  return { dynamic, static: static_vars };
};

/**
 * Format variable preview based on type
 * @param {*} value - Variable value
 * @param {string} dataType - Data type (string, number, currency, date_time)
 * @returns {string} Formatted preview
 */
export const formatVariablePreview = (value, dataType) => {
  if (value === null || value === undefined) return '';

  switch (dataType) {
    case 'number':
      return value.toString();
    case 'currency':
      // If it's already formatted (like "$100.00"), return as is
      if (typeof value === 'string') return value;
      return `$${parseFloat(value).toFixed(2)}`;
    case 'date_time':
      if (typeof value === 'string') return value;
      if (value instanceof Date) {
        return value.toISOString();
      }
      return value.toString();
    case 'string':
    default:
      return value.toString();
  }
};

/**
 * Validate all variables are mapped
 * @param {Array<string>} variables - Array of variable positions
 * @param {Object} mappings - Variable mappings object
 * @returns {boolean} True if all variables are mapped
 */
export const validateVariableMappings = (variables, mappings) => {
  const errors = [];
  const warnings = [];

  // No variables means no validation needed
  if (!variables || variables.length === 0) {
    return { isValid: true, errors, warnings };
  }

  // If there are variables but no mappings at all
  if (!mappings || Object.keys(mappings).length === 0) {
    errors.push(
      'Please configure variable mappings for all detected variables'
    );
    return { isValid: false, errors, warnings };
  }

  // Check each variable
  variables.forEach(key => {
    const mapping = mappings[key];
    const varDisplay = `{{${key}}}`;

    if (!mapping) {
      errors.push(`Variable ${varDisplay} needs to be configured`);
      return;
    }

    // Check required fields
    if (!mapping.type) {
      errors.push(
        `Variable ${varDisplay}: Select whether it's dynamic or static`
      );
      return;
    }

    if (!mapping.data_type) {
      errors.push(
        `Variable ${varDisplay}: Select a data type (text, number, currency, or date)`
      );
      return;
    }

    // Dynamic variables must have source
    if (mapping.type === 'dynamic' && !mapping.source) {
      errors.push(
        `Variable ${varDisplay}: Select a data source for this dynamic variable`
      );
      return;
    }

    // Dynamic variables must have fallback_value
    if (
      mapping.type === 'dynamic' &&
      (!mapping.fallback_value || mapping.fallback_value.trim() === '')
    ) {
      errors.push(
        `Variable ${varDisplay}: Provide a fallback value (used when data source is empty, required for WhatsApp approval)`
      );
    }

    // Static variables must have example_value
    if (
      mapping.type === 'static' &&
      (!mapping.example_value || mapping.example_value.trim() === '')
    ) {
      errors.push(
        `Variable ${varDisplay}: Provide an example value (required for WhatsApp approval)`
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Get available dynamic sources for variable mapping
 * @returns {Object} Available sources organized by category
 */
export const getDynamicSources = () => ({
  contact: [
    { value: 'contact.first_name', label: 'First Name' },
    { value: 'contact.last_name', label: 'Last Name' },
    { value: 'contact.name', label: 'Full Name' },
    { value: 'contact.email', label: 'Email' },
    { value: 'contact.phone_number', label: 'Phone Number' },
    { value: 'contact.identifier', label: 'Identifier' },
    { value: 'contact.id', label: 'Contact ID' },
  ],
  conversation: [
    { value: 'conversation.id', label: 'Conversation ID' },
    { value: 'conversation.display_id', label: 'Conversation Display ID' },
  ],
  agent: [
    { value: 'agent.first_name', label: 'Agent First Name' },
    { value: 'agent.last_name', label: 'Agent Last Name' },
    { value: 'agent.name', label: 'Agent Full Name' },
    { value: 'agent.email', label: 'Agent Email' },
  ],
});

/**
 * Get data type options
 * @returns {Array<Object>} Data type options
 */
export const getDataTypeOptions = () => [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'date_time', label: 'Date/Time' },
];

/**
 * Get variable type options
 * @returns {Array<Object>} Variable type options
 */
export const getVariableTypeOptions = () => [
  { value: 'dynamic', label: 'Dynamic (Auto-filled from contact data)' },
  { value: 'static', label: 'Static (Filled by agent when sending)' },
];

/**
 * Create default variable mapping
 * @returns {Object} Default mapping object
 */
export const createDefaultMapping = () => ({
  type: 'dynamic',
  source: '',
  data_type: 'string',
  fallback_value: '',
  example_value: '',
});

/**
 * Group dynamic sources by category
 * @returns {Array<Object>} Grouped sources
 */
export const getGroupedDynamicSources = () => {
  const sources = getDynamicSources();

  return [
    {
      label: 'Contact Fields',
      options: sources.contact,
    },
    {
      label: 'Conversation Fields',
      options: sources.conversation,
    },
    {
      label: 'Agent Fields',
      options: sources.agent,
    },
  ];
};

/**
 * Determine appropriate text color based on background color brightness
 * @param {string} bgColorClass - Tailwind background color class (e.g., 'bg-blue-50', 'bg-gray-800', 'bg-blue-50 dark:bg-blue-950/20')
 * @returns {string} Tailwind text color class for optimal contrast
 */
export const getContrastTextColor = bgColorClass => {
  if (!bgColorClass || typeof bgColorClass !== 'string') {
    return 'text-n-slate-12'; // Default dark text
  }

  // Handle compound classes like 'bg-blue-50 dark:bg-blue-950/20'
  // Extract light mode and dark mode background classes separately
  const lightModeMatch = bgColorClass.match(/\bbg-[a-z]+-(\d+)\b/);
  const darkModeMatch = bgColorClass.match(/dark:bg-[a-z]+-(\d+)/);

  if (!lightModeMatch) {
    return 'text-n-slate-12'; // Default dark text for unrecognized formats
  }

  const lightIntensity = parseInt(lightModeMatch[1], 10);

  // Tailwind color scale reference:
  // 50-100: Very light colors (need dark text)
  // 200-500: Medium colors (typically need dark text)
  // 600-900: Dark colors (need light text)
  // 950: Very dark colors (need light text)

  // Determine light mode text color
  let lightText = 'text-n-slate-12';
  if (lightIntensity >= 600) {
    lightText = 'text-white';
  } else if (lightIntensity <= 200) {
    lightText = 'text-n-slate-12';
  } else {
    // Medium shades (300-500)
    lightText = 'text-n-slate-12';
  }

  // Determine dark mode text color
  let darkText = 'dark:text-n-slate-12';
  if (darkModeMatch) {
    const darkIntensity = parseInt(darkModeMatch[1], 10);
    // Dark mode backgrounds are usually very dark (950) or transparent, so use light text
    if (darkIntensity >= 600) {
      darkText = 'dark:text-white';
    } else {
      darkText = 'dark:text-n-slate-12';
    }
  } else {
    // If no dark mode class specified, mirror light mode logic
    darkText =
      lightIntensity >= 600 ? 'dark:text-white' : 'dark:text-n-slate-12';
  }

  return `${lightText} ${darkText}`;
};
